import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateArtifactContext } from "../../../../context/CreateArtifactContext";
import {
  ContentState,
  ContentSubstate,
  SKArtifact,
  SKAudioArtifact,
  TopicTypeString,
  getArtefactIcon,
  getContentCreatorActionableItems,
} from "../../../../models/SKArtifact";
import { useNavigationBar } from "../../../../context/NavigationContext";
import { capitalizeFirstLetter } from "../../../../utils/stringFunctions";
import { ChoosePublishingSequence } from "./components/ChoosePublishingSequence";
import { ChoosePublishingTier } from "./components/ChoosePublishingTier";
import { AddContentItem } from "./components/AddContentItem";
import { CertifyArtifact } from "./components/CertifyArtifact";
import ArtifactAgeRating from "./components/ArtifactAgeRating";
import ArtifactTopics from "./components/ArtifactTopics";
import * as Yup from "yup";
import { AddTextField } from "../../../../components/AddTextField";
import { AddTextAreaField } from "../../../../components/AddTextAreaField";
import { ContentStatusView } from "./components/ContentStatusView";
import ContentHistoryNavigator from "./components/ContentHistoryNavigator";
import ContextCheckListCard from "./components/ContextCheckListCard";
import { PublishingTier } from "./components/PublishingTier";
import { ArtifactContainerRef } from "./CreateArtifact";
import ContentEditNavigator from "../../../contentModeration/ContentEditNavigator";
import ContentReportNavigator from "./components/ContentReportNavigator";
import { useAuth, UserRole } from "../../../../context/AuthContext";

const AudioArtifactContainer = forwardRef<ArtifactContainerRef>((props, ref) => {
  const [isValidForm, setIsValidForm] = useState(false);
  // const [isEdit, setIsEdit] = useState(false);
  const context = useAuth();
  const { contentData, updateContentData, artifactId, isEditable, isModerator, isContentEditing } = useContext(CreateArtifactContext);

  const [error, setErrorState] = useState<{ [key: string]: string | undefined }>({});

  const setErrors = (newErrors: { [key: string]: string | undefined }) => {
    setErrorState(newErrors);
  };

  useImperativeHandle(ref, () => ({
    containerSchema: () => schema,
    setErrors: newErrors => setErrors(newErrors),
  }));

  const navigate = useNavigate();
  const { setNavProps } = useNavigationBar();

  const schema = Yup.object().shape({
    type: Yup.string().required(),
    title: Yup.string().required().max(50),
    subtitle: Yup.string().notRequired().max(500),
    text: Yup.string().notRequired().max(500),
    topic: Yup.string().required(),
    tier: Yup.string().required(),
    age: Yup.number().required(),
    status: Yup.string().required(),
    coverMedia: Yup.object({
      coverImageURL: Yup.string().required("Cover image is required"),
      coverVideoURL: Yup.string().notRequired(),
      coverAudioURL: Yup.string().notRequired(),
    }).required("Cover media is required"),

    certifyFirst: Yup.boolean().oneOf([true]).required("Please certify content"),
    certifySecond: Yup.boolean().oneOf([true]).required("Please certify content"),
    audioURL: Yup.string().required(),
  });

  // useEffect(() => {
  //   contentData.status != ContentState.IN_REVIEW ? setIsEdit(false) : setIsEdit(true);
  // }, [contentData]);

  return (
    <>
      <div className="flex flex-col gap-[30px] mt-[10px]">
        {isModerator &&
        !isContentEditing &&
        artifactId &&
        contentData.status === ContentState.ACTIVE &&
        contentData.subStatus === ContentSubstate.EDIT_IN_REVIEW ? (
          <ContentEditNavigator title="Channel Edit" docId={artifactId!} />
        ) : (
          ""
        )}

        {artifactId && (contentData as SKArtifact).hasHistory ? <ContentHistoryNavigator title="Channel Status" contentId={artifactId} /> : ""}
        {artifactId && context?.userRoles?.includes(UserRole.SkarvoAdmin) && contentData.reportCount && contentData.reportCount > 0 ? (
          <ContentReportNavigator title="Content Reports" />
        ) : (
          ""
        )}
        {/* <div className="artifact-page-context">
        {artifactId && contentData.status != State.review ? (
          <ContentHistoryNavigator
            title="Content Status"
            contentId={artifactId}
          />
        ) : (
          ""
        )} */}

        <AddContentItem
          index={1}
          onClick={() => {
            navigate("addAudio");
          }}
          count={(contentData as SKAudioArtifact)?.audioURL ? 1 : 0}
        />

        <AddTextField
          name={"addtitle"}
          title={"Add Title"}
          note={"Title can be the subject, purpose, theme or topic of your post."}
          placeholder={"Enter Title"}
          index={2}
          charAllowed={100}
          value={(contentData as SKArtifact).title}
          onChange={async e => {
            updateContentData({ title: e.target.value! });
          }}
          disabled={!isEditable}
          error={error?.title}
        />

        <AddTextAreaField
          name={"addtext"}
          title={"Add Text"}
          note={"Maximum text length of 500 characters."}
          placeholder={"Enter Text"}
          index={3}
          height={150}
          charAllowed={200}
          value={(contentData as SKArtifact).subtitle as string}
          onChange={async e => {
            updateContentData({ subtitle: e.target.value! });
          }}
          disabled={!isEditable}
        />

        {/* <ArtifactTopics index={4} name={"Choose Content Topic"} title={"Choose Content Topic"} />
        <ArtifactAgeRating index={5} name={"Choose Content Age Rating"} title={"Choose Content Age Rating"} />
        <CertifyArtifact index={6} name={"Certify Content Safety"} title={"Certify Content Safety"} />
        <ChoosePublishingTier index={7} /> */}
        {/* <ChoosePublishingSequence index={8} /> */}

        <ContextCheckListCard
          index={4}
          name="Add Topic"
          title="Choose the primary topic of your Content."
          purposeText="Add Topic"
          notes="Choose the topic that represents the primary focus of most of your Content."
          doneText={contentData.topic ? capitalizeFirstLetter(TopicTypeString[contentData.topic]) : undefined}
          action={() => {
            navigate("selectTopic");
          }}
          disabled={!isEditable}
        />

        <ContextCheckListCard
          index={5}
          name="Add Age Rating"
          title="Choose the Age Rating of your Content"
          purposeText="Add Age Rating"
          notes="Choose the age rating based on the appropriateness of your Content for children."
          doneText={contentData.age ? "Age " + contentData.age + "+" : undefined}
          action={() => {
            navigate("ageRating");
          }}
          disabled={!isEditable}
        />

        <ContextCheckListCard
          index={6}
          name="Certify Content"
          title="Certify Content"
          purposeText="Certify Content"
          notes="Certify that your Content would ensure and comply with these requirements."
          doneText={contentData.certifyFirst && contentData.certifySecond ? "Certified" : undefined}
          action={() => {
            navigate("certifyContent");
          }}
          disabled={!isEditable}
        />
        <PublishingTier index={7} />
      </div>
    </>
  );
});

export default AudioArtifactContainer;
